<template>
	<div class="bg-gray rounded-xl pt-3 px-3 pb-0 overflow-clip">
		<div class="flex items-center">
			<h1 class="text-base font-bold">Sacrament Attendance</h1>
			<div class="ml-auto flex gap-2">
				<b-dropdown class="mt-1" variant="link" right toggle-class="text-decoration-none" no-caret>
					<template #button-content>
						<span class="flex item-center w-full">
							<span class="text-gray-1 text-xs">{{ filterBy.label }} </span> <church-icon
								class="!w-3 relative -top-1" icon="chevron-down" />
						</span>
					</template>

					<b-dropdown-item v-for="filter in modifiedDateFilter" :key="filter.value"
						@click="() => (filterBy = filter, rerenderChart = Math.random())">{{ filter.label
						}}</b-dropdown-item>
				</b-dropdown>

				<button class="text-black -mt-1" @click="() => showAttendanceList = true">
					<church-icon class="!w-4 !text-blue !text-black" icon="pencil" />
				</button>
			</div>
		</div>

		<div class="mb-3">
			<button class="text-sm px-2 py-0.5 rounded-lg mr-2 bg-gray-2">Overall</button>
			<button class="text-sm px-2 py-0.5 rounded-lg mr-2 ">By ward</button>
		</div>

		<div>
			<h1 class="text-2xl mb-0 font-bold">{{ attendancePercentage }}%</h1>
			<p v-if="filterBy.value != 'all'" class="text-gray-1 text-sm mb-0 lowercase"><span class="text-green ">{{
				attendanceRate > 0 ? '+' : '' }}{{ attendanceRate }}%</span> vs. last
				{{ filterBy.label }}
			</p>
		</div>

		<div class="mt-2" :key="rerenderChart" v-node="renderLineChartSecramentAttendance()"></div>


		<m-add-sacrament-attendance @close="() => showAddAttendance = false" ref="addAttendanceRef"
			@change="nextProcess" :attendanceList="attendanceList" :show="showAddAttendance" :attendance="attendance"
			:wards="wardsList" />


		<m-sacrament-attendance-list-pop class="!z-40" :show="showAttendanceList" :attendanceList="attendanceList"
			:wards="wardsList" @edit="(item) => editRecord(item)" @delete="(item) => toDeleteAttendance = item"
			@add="() => showAddNewRecord()" @close="() => showAttendanceList = false" />

		<m-delete-confirmation-pop :showPop="toDeleteAttendance ? true : false" title="Delete Attendance Record"
			description="Are you sure you want to delete this attendance record?"
			@continueDelete="() => startDeletingAttendance()" />
	</div>
</template>

<script setup>
import WardApis from '@/apis/wardApis';
import Charts from '@/helpers/charts';
import DateUtils from '@/helpers/dateUtls';
import { computed, onBeforeMount, ref } from 'vue';
import SacramentApis from '@/apis/sacramentApis';
import StringParser from '@/helpers/stringParser';
import { dateFilter } from '@/helpers/staticVariables';
import moment from 'moment';


/* ========= STATEs ======== */
const filterBy = ref(dateFilter[0]);
const defaultAttendance = () => {
	return {
		id: null,
		total_number_of_attendees: 0,
		number_of_attendees: 0,
		ward: '',
		date_attendance: DateUtils.dateFormat({ date: new Date(), format: 'MM/DD/YYYY', timeZone: false })
	}
}
const attendance = ref(defaultAttendance());
const wardsList = ref([])
const attendanceList = ref([]);
const showAttendanceList = ref(false);
const showAddAttendance = ref(false);
const addAttendanceRef = ref(null);
const toDeleteAttendance = ref(null);
const rerenderChart = ref(Math.random());

/* =========== COMPUTED ======== */

/**
 * Modified date filter
 * add 6 months between quarter and year
 */
const modifiedDateFilter = computed(() => {
	// Create a copy of the dateFilter array
	const modifiedFilter = [...dateFilter];

	// Find the index of the quarter option
	const quarterIndex = modifiedFilter.findIndex(filter => filter.value === 'quarter');

	// Insert the 6 months option after the quarter option
	if (quarterIndex !== -1) {
		modifiedFilter.splice(quarterIndex + 1, 0, { label: "6 Months", value: '6months' });
	}

	return modifiedFilter;
})


/**
 * Filtered attendace
 */
const filteredAttendanceList = computed(() => {
	return attendanceList.value?.filter(item => {
		if (filterBy.value?.value == 'month') {
			return DateUtils.isSame({ date: item.date_of_attendance, compareDate: new Date(), unit: 'month', timeZone: false, dateOriginFormat: 'DD/MM/YYYY' });
		}
		if (filterBy.value?.value == 'quarter') {
			return DateUtils.dateDiff({ startDate: item.date_of_attendance, endDate: new Date(), unit: 'months', timeZone: null, startDateOriginFomat: 'DD/MM/YYYY' }) <= 3;
		}
		if (filterBy.value?.value == '6months') {
			return DateUtils.dateDiff({ startDate: item.date_of_attendance, endDate: new Date(), unit: 'months', timeZone: null, startDateOriginFomat: 'DD/MM/YYYY' }) <= 6;
		}
		if (filterBy.value?.value == 'year') {
			return DateUtils.isSame({ date: item.date_of_attendance, compareDate: new Date(), unit: 'year', timeZone: false, dateOriginFormat: 'DD/MM/YYYY' });
		}
		if (filterBy.value?.value == 'all') {
			return true;
		}
		return false;
	})
});

/**
 * Get attendance percentage
 */
const attendancePercentage = computed(() => {
	const attendances = [...filteredAttendanceList.value]
	const totalAttendance = attendances.reduce((sum, item) => {
		return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.total_number_of_attendees || 0), 0) || 0);
	}, 0);
	const totalNumberOfAttendees = attendances.reduce((sum, item) => {
		return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.number_of_attendees || 0), 0) || 0);
	}, 0);
	return totalNumberOfAttendees && totalAttendance ? Math.round((totalNumberOfAttendees / totalAttendance) * 100) : 0;
})


/**
 * Compute attandance increase, decrease percentage basse on filterBy
 * if filterBy is month, it will compare with last month
 * if filterBy is quarter, it will compare with last quarter
 * if filterBy is year, it will compare with last year
 * if filterBy is all, it will compare with last month
 */

const attendanceRate = computed(() => {
	if (!attendanceList.value || !attendanceList.value.length) return 0;

	if (filterBy.value.value == 'month') {
		const lastMonthTotalAttendees = attendanceList.value
			.filter(item => DateUtils.isSame({ date: item.date_of_attendance, compareDate: moment().subtract(1, 'month'), unit: 'month', timeZone: false, dateOriginFormat: 'DD/MM/YYYY' }))
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.total_number_of_attendees || 0), 0) || 0);
			}, 0);

		const lastMonthNumberOfAttendees = attendanceList.value
			.filter(item => DateUtils.isSame({ date: item.date_of_attendance, compareDate: moment().subtract(1, 'month'), unit: 'month', timeZone: false, dateOriginFormat: 'DD/MM/YYYY' }))
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.number_of_attendees || 0), 0) || 0);
			}, 0);

		const lastMonthAttendancePercentage = lastMonthTotalAttendees && lastMonthNumberOfAttendees ? Math.round((lastMonthNumberOfAttendees / lastMonthTotalAttendees) * 100) : 0;

		const currentMonthTotalAttendees = attendanceList.value
			.filter(item => DateUtils.isSame({ date: item.date_of_attendance, compareDate: new Date(), unit: 'month', timeZone: false, dateOriginFormat: 'DD/MM/YYYY' }))
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.total_number_of_attendees || 0), 0) || 0);
			}, 0);
		const currentMonthNumberOfAttendees = attendanceList.value
			.filter(item => DateUtils.isSame({ date: item.date_of_attendance, compareDate: new Date(), unit: 'month', timeZone: false, dateOriginFormat: 'DD/MM/YYYY' }))
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.number_of_attendees || 0), 0) || 0);
			}, 0);
		const currentMonthAttendancePercentage = currentMonthTotalAttendees && currentMonthNumberOfAttendees ? Math.round((currentMonthNumberOfAttendees / currentMonthTotalAttendees) * 100) : 0;

		console.log('lastMonthAttendancePercentage', lastMonthAttendancePercentage, 'currentMonthAttendancePercentage', currentMonthAttendancePercentage)

		/**
		 * Return + or - percentage
		 */
		if (lastMonthAttendancePercentage === 0) {
			return currentMonthAttendancePercentage > 0 ? currentMonthAttendancePercentage : 0;
		}

		return Math.round(((currentMonthAttendancePercentage - lastMonthAttendancePercentage) / lastMonthAttendancePercentage) * 100);
	}

	/**
	 * Quarterly attendance rate
	 */
	if (filterBy.value.value == 'quarter') {
		const lastQuarterTotalAttendees = attendanceList.value
			.filter(item => DateUtils.dateDiff({ startDate: item.date_of_attendance, endDate: moment().subtract(1, 'quarter'), unit: 'months', timeZone: false, startDateOriginFomat: 'DD/MM/YYYY' }) <= 3)
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.total_number_of_attendees || 0), 0) || 0);
			}, 0);

		const lastQuarterNumberOfAttendees = attendanceList.value
			.filter(item => DateUtils.dateDiff({ startDate: item.date_of_attendance, endDate: moment().subtract(1, 'quarter'), unit: 'months', timeZone: false, startDateOriginFomat: 'DD/MM/YYYY' }) <= 3)
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.number_of_attendees || 0), 0) || 0);
			}, 0);

		const lastQuarterAttendancePercentage = lastQuarterTotalAttendees && lastQuarterNumberOfAttendees ? Math.round((lastQuarterNumberOfAttendees / lastQuarterTotalAttendees) * 100) : 0;

		const currentQuarterTotalAttendees = attendanceList.value
			.filter(item => DateUtils.dateDiff({ startDate: item.date_of_attendance, endDate: new Date(), unit: 'months', timeZone: false, startDateOriginFomat: 'DD/MM/YYYY' }) <= 3)
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.total_number_of_attendees || 0), 0) || 0);
			}, 0);
		const currentQuarterNumberOfAttendees = attendanceList.value
			.filter(item => DateUtils.dateDiff({ startDate: item.date_of_attendance, endDate: new Date(), unit: 'months', timeZone: false, startDateOriginFomat: 'DD/MM/YYYY' }) <= 3)
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.number_of_attendees || 0), 0) || 0);
			}, 0);
		const currentQuarterAttendancePercentage = currentQuarterTotalAttendees && currentQuarterNumberOfAttendees ? Math.round((currentQuarterNumberOfAttendees / currentQuarterTotalAttendees) * 100) : 0;
		console.log('lastQuarterAttendancePercentage', lastQuarterAttendancePercentage, 'currentQuarterAttendancePercentage', currentQuarterAttendancePercentage)
		/**
		 * Return + or - percentage
		 */
		if (lastQuarterAttendancePercentage === 0) {
			return currentQuarterAttendancePercentage > 0 ? currentQuarterAttendancePercentage : 0;
		}
		return Math.round(((currentQuarterAttendancePercentage - lastQuarterAttendancePercentage) / lastQuarterAttendancePercentage) * 100);
	}

	/**
	 * 6 Months attendance rate
	 */
	if (filterBy.value.value == '6months') {
		const last6MonthsTotalAttendees = attendanceList.value
			.filter(item => DateUtils.isSameOrBefore({ date: item.date_of_attendance, compareDate: moment().subtract(6, 'months'), unit: 'months', timeZone: false, dateOriginFormat: 'DD/MM/YYYY' }))
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.total_number_of_attendees || 0), 0) || 0);
			}, 0);

		const last6MonthsNumberOfAttendees = attendanceList.value
			.filter(item => DateUtils.dateDiff({ startDate: item.date_of_attendance, endDate: moment().subtract(6, 'months'), unit: 'months', timeZone: false, startDateOriginFomat: 'DD/MM/YYYY' }) <= 6)
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.number_of_attendees || 0), 0) || 0);
			}, 0);

		const last6MonthsAttendancePercentage = last6MonthsTotalAttendees && last6MonthsNumberOfAttendees ? Math.round((last6MonthsNumberOfAttendees / last6MonthsTotalAttendees) * 100) : 0;

		const current6MonthsTotalAttendees = attendanceList.value
			.filter(item => DateUtils.dateDiff({ startDate: item.date_of_attendance, endDate: new Date(), unit: 'months', timeZone: false, startDateOriginFomat: 'DD/MM/YYYY' }) <= 6)
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.total_number_of_attendees || 0), 0) || 0);
			}, 0);
		const current6MonthsNumberOfAttendees = attendanceList.value
			.filter(item => DateUtils.dateDiff({ startDate: item.date_of_attendance, endDate: new Date(), unit: 'months', timeZone: false, startDateOriginFomat: 'DD/MM/YYYY' }) <= 6)
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum,
					report) => sum + Number(report?.number_of_attendees || 0), 0) || 0);
			}, 0);

		const current6MonthsAttendancePercentage = current6MonthsTotalAttendees && current6MonthsNumberOfAttendees ? Math.round((current6MonthsNumberOfAttendees / current6MonthsTotalAttendees) * 100) : 0;
		console.log('last6MonthsAttendancePercentage', last6MonthsAttendancePercentage, 'current6MonthsAttendancePercentage', current6MonthsAttendancePercentage)


		/**
		 * Return + or - percentage
		 */
		if (last6MonthsAttendancePercentage === 0) {
			return current6MonthsAttendancePercentage > 0 ? current6MonthsAttendancePercentage : 0;
		}
		return Math.round(((current6MonthsAttendancePercentage - last6MonthsAttendancePercentage) / last6MonthsAttendancePercentage) * 100);
	}

	/**
	 * Yearly attendance rate
	 */
	if (filterBy.value.value == 'year') {
		const lastYearTotalAttendees = attendanceList.value
			.filter(item => DateUtils.isSame({ date: item.date_of_attendance, compareDate: moment().subtract(1, 'year'), unit: 'year', timeZone: false, dateOriginFormat: 'DD/MM/YYYY' }))
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.total_number_of_attendees || 0), 0) || 0);
			}, 0);

		const lastYearNumberOfAttendees = attendanceList.value
			.filter(item => DateUtils.isSame({ date: item.date_of_attendance, compareDate: moment().subtract(1, 'year'), unit: 'year', timeZone: false, dateOriginFormat: 'DD/MM/YYYY' }))
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.number_of_attendees || 0), 0) || 0);
			}, 0);

		const lastYearAttendancePercentage = lastYearTotalAttendees && lastYearNumberOfAttendees ? Math.round((lastYearNumberOfAttendees / lastYearTotalAttendees) * 100) : 0;

		const currentYearTotalAttendees = attendanceList.value
			.filter(item => DateUtils.isSame({ date: item.date_of_attendance, compareDate: new Date(), unit: 'year', timeZone: false, dateOriginFormat: 'DD/MM/YYYY' }))
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.total_number_of_attendees || 0), 0) || 0);
			}, 0);
		const currentYearNumberOfAttendees = attendanceList.value
			.filter(item => DateUtils.isSame({ date: item.date_of_attendance, compareDate: new Date(), unit: 'year', timeZone: false, dateOriginFormat: 'DD/MM/YYYY' }))
			.reduce((sum, item) => {
				return sum + (item?.attendance_report?.reduce((sum, report) => sum + Number(report?.number_of_attendees || 0), 0) || 0);
			}, 0);
		const currentYearAttendancePercentage = currentYearTotalAttendees && currentYearNumberOfAttendees ? Math.round((currentYearNumberOfAttendees / currentYearTotalAttendees) * 100) : 0;
		console.log('lastYearAttendancePercentage', lastYearAttendancePercentage, 'currentYearAttendancePercentage', currentYearAttendancePercentage)
		/**
		 * Return + or - percentage
		 */
		if (lastYearAttendancePercentage === 0) {
			return currentYearAttendancePercentage > 0 ? currentYearAttendancePercentage : 0;
		}
		return Math.round(((currentYearAttendancePercentage - lastYearAttendancePercentage) / lastYearAttendancePercentage) * 100);
	}

	return attendancePercentage.value || 0
})

/* ========= METHODS ======== */

/**
 * Show add new record pop
 * 
 */
const showAddNewRecord = () => {
	console.log('addAttendanceRef.value?.setAttendance', addAttendanceRef.value?.setAttendance)
	addAttendanceRef.value?.setAttendance?.(attendance.value)
	showAddAttendance.value = true;

}

/**
 * Edit record
 * @param {Object} item
 */
const editRecord = (item) => {
	attendance.value = { ...item };
	showAddAttendance.value = true;
	addAttendanceRef.value?.setAttendance?.(attendance.value)
}

/**
 * Group attendance by month or date if data has 1 month data only
 * @param {Array} data
 * @returns {Array}
 */
const groupAttendanceByMonth = (data) => {
	if (!data || !data.length) return [];
	let groupedData = {};
	let datas = [];

	/* This month data */
	if (filterBy.value?.value == 'month') {
		datas = [...data]
			.filter(item => DateUtils.isSame({ date: item.date_of_attendance, compareDate: new Date(), unit: 'month', timeZone: false, dateOriginFormat: 'DD/MM/YYYY' }))
			.map(item => {
				const date = DateUtils.dateFormat({ date: item.date_of_attendance, format: 'MM/DD/YYYY', timeZone: false, options: { originDateFormat: 'DD/MM/YYYY' } });

				return {
					Date: new Date(date),
					count: item?.attendance_report?.reduce((sum, report) => sum + Number(report?.number_of_attendees || 0), 0) || 0
				}
			})
			.sort((a, b) => a.Date - b.Date)


		console.log('this month datas', datas);

	}


	/* Group by months */
	if (filterBy.value?.value != 'month') {
		groupedData = data.reduce((acc, item) => {
			const date = DateUtils.setDate({ date: item.date_of_attendance, day: 1, originDateFormat: 'DD/MM/YYYY', format: 'MM/DD/YYYY', timeZone: false });

			console.log(date, 'item', item)

			if (!acc[date]) {
				acc[date] = { date, count: 0 };
			}

			acc[date].count += item?.attendance_report?.reduce((sum, report) => sum + Number(report?.number_of_attendees || 0), 0) || 0;
			return acc;
		}, {});


		datas = Object.values(groupedData).map(item => ({
			Date: new Date(item.date),
			count: item.count
		}))
			.sort((a, b) => a.Date - b.Date);
	}


	return datas;
}

/**
* Render line chart Sacrament Attendance
*/
const renderLineChartSecramentAttendance = () => {
	const data = groupAttendanceByMonth(filteredAttendanceList.value);

	console.log('month datas', data)

	return Charts.generateLineChart({
		width: window.innerWidth - 64,
		datas: data
	});
}

/**
 * Delete attendance record
 */
const startDeletingAttendance = () => {
	if (!toDeleteAttendance.value) return;
	const params = {
		ward: toDeleteAttendance?.value?.ward,
		date_attendance: DateUtils.dateFormat({ date: toDeleteAttendance?.value?.date_attendance, format: 'YYYY-MM-DD', timeZone: false })
	}
	SacramentApis.deleteAttendanceRecord({
		params, callback: () => {
			attendanceList.value = attendanceList.value?.map(item => {
				item.attendance_report = item.attendance_report?.filter(at => at.id != toDeleteAttendance.value?.id);
				return item;
			})
			toDeleteAttendance.value = null;
		}
	});
}

/**
 * Add | update attendance record
 */
const nextProcess = () => {
	/* Reset Attendance */
	addAttendanceRef.value?.toggleProcessing?.(true)
	const resetAttendance = () => {
		attendance.value = { ...defaultAttendance() };
		addAttendanceRef.value?.setAttendance?.(attendance.value)
		showAddAttendance.value = false;
		addAttendanceRef.value?.toggleProcessing?.(false);
	}

	if (attendance.value?.id) {
		// Update record
		const params = { ...attendance.value }
		delete params.id;
		delete params.select_ward;
		params.date_attendance = DateUtils.dateFormat({ date: attendance.value.date_attendance, format: 'YYYY-MM-DD', timeZone: false })
		SacramentApis.updateAttendanceRecord({
			params, callback: () => {
				console.log('attendance.value', attendance.value)
				attendanceList.value = [...attendanceList.value]?.map(item => {
					item.attendance_report = item.attendance_report?.map(at => {
						if (at.id == attendance.value.id) {
							return {
								...at,
								...attendance.value
							}
						}
						return at;
					})
					return item;
				})
				resetAttendance();
			}
		})
		return;
	}

	// Add record
	const params = { ...attendance.value }
	delete params.id;
	params.date_attendance = DateUtils.dateFormat({ date: attendance.value.date_attendance, format: 'YYYY-MM-DD', timeZone: false })

	SacramentApis.addAttendanceRecord({
		params, callback: data => {
			/**
			 * Add id to attendance reports
			 */
			attendanceList.value = data?.map?.(item => {
				return {
					...item,
					attendance_report: item.attendance_report?.map?.(at => {
						return {
							...at,
							id: StringParser.makeString(5),
						}
					})
				}
			})
			resetAttendance();
		}
	})


}

/* ========== ON ONLOAD ========*/
onBeforeMount(async () => {
	WardApis.getAllWards({ callback: wards => wardsList.value = wards?.wards?.value || [] })
	await SacramentApis.getAttendanceRecords({
		callback: data => attendanceList.value = data.map(d => {
			return {
				...d,
				attendance_report: d.attendance_report?.map(at => {
					return {
						...at,
						id: StringParser.makeString(5),
					}
				})
			}
		}) || []
	});

	/* Rerender chart */
	rerenderChart.value = Math.random();
})
</script>

<style lang="scss">
.sacramentDate {
	&.mx-input {
		height: 42px;
	}
}
</style>